import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DocExpiryBadge } from "@/components/StatusBadge";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Send, Bell, Mail, BellRing, CheckCircle2 } from "lucide-react";

export default function Alertas() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [s, p, l] = await Promise.all([
      api.get("/alerts/status"),
      api.get("/alerts/preview"),
      api.get("/alerts/log"),
    ]);
    setStatus(s.data);
    setPreview(p.data);
    setLogs(l.data);
  };

  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true); setMsg("");
    try {
      const r = await api.post("/alerts/send-now");
      const result = r.data;
      if (result.status === "no_alerts") setMsg("No hay alertas que enviar — todo al día.");
      else if (result.status === "no_recipient") setMsg("No se encontró correo de destino. Configura el correo de la empresa en /configuracion.");
      else if (result.result?.mocked) setMsg(`MOCK · Alerta registrada para ${result.recipient}. Configura RESEND_API_KEY para enviar real.`);
      else setMsg(`Correo enviado a ${result.recipient}.`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Error");
    }
    setSending(false);
  };

  if (!status || !preview) return <div className="p-8">Cargando...</div>;

  return (
    <div data-testid="alertas-page">
      <PageHeader
        tag="Plan Pro"
        title="Alertas Automáticas"
        description="Notificaciones por correo cuando documentos están por vencer. El sistema chequea diariamente y envía un resumen ejecutivo a tu empresa."
        action={
          <button
            onClick={sendNow}
            disabled={sending || preview.count === 0}
            className="h-touch px-6 bg-blue-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-700 disabled:opacity-50 active:scale-[0.98] flex items-center gap-2"
            data-testid="send-alerts-now-btn"
          >
            <Send size={16} strokeWidth={2.5} />
            {sending ? "Enviando..." : "Enviar Ahora"}
          </button>
        }
      />

      {/* Status */}
      <div className="grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200 mb-8">
        <div className="bg-white p-6">
          <p className="veta-tag flex items-center gap-2"><Mail size={12} strokeWidth={2.5} /> Estado</p>
          <p className={`font-display text-2xl mt-2 ${status.live ? "text-green-700" : "text-orange-600"}`} data-testid="alerts-status-badge">
            {status.live ? "ACTIVO · LIVE" : "MODO MOCK"}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            {status.live
              ? "Resend configurado · los correos se envían realmente."
              : "Los correos se simulan y registran en logs. Agrega RESEND_API_KEY a backend/.env para activar."}
          </p>
        </div>
        <div className="bg-white p-6">
          <p className="veta-tag flex items-center gap-2"><BellRing size={12} strokeWidth={2.5} /> Frecuencia</p>
          <p className="font-display text-2xl mt-2">Cada {status.interval_hours}h</p>
          <p className="text-xs text-gray-500 mt-2">El scheduler corre automáticamente en background.</p>
        </div>
        <div className="bg-white p-6">
          <p className="veta-tag flex items-center gap-2"><Bell size={12} strokeWidth={2.5} /> Pendientes ahora</p>
          <p className="font-display text-4xl mt-2" data-testid="alerts-preview-count">{preview.count}</p>
          <p className="text-xs text-gray-500 mt-2">Documentos vencidos o por vencer en 30 días.</p>
        </div>
      </div>

      {msg && (
        <div className="status-success p-4 mb-6 text-sm flex items-center gap-2" data-testid="alerts-result-msg">
          <CheckCircle2 size={16} strokeWidth={2.5} /> {msg}
        </div>
      )}

      {/* Preview items */}
      <div className="border border-gray-200 mb-10">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <p className="veta-tag">Vista previa · contenido de la próxima alerta</p>
          {preview.count > 0 && <span className="text-xs text-gray-500">{preview.count} item(s)</span>}
        </div>
        {preview.count === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            <CheckCircle2 size={32} strokeWidth={2.5} className="text-green-600 mx-auto mb-2" />
            Sin documentos por vencer en los próximos 30 días.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Patente</Th>
                <Th>Documento</Th>
                <Th>Vence</Th>
                <Th>Estado</Th>
              </tr>
            </thead>
            <tbody>
              {preview.items.map((it, i) => (
                <tr key={i} className="border-b border-gray-200" data-testid={`alert-preview-${i}`}>
                  <td className="px-4 py-3 font-mono font-bold">{it.plate}</td>
                  <td className="px-4 py-3 text-sm">{it.doc_type}</td>
                  <td className="px-4 py-3 text-sm font-mono">{fmtDate(it.expiry_date)}</td>
                  <td className="px-4 py-3"><DocExpiryBadge expiryDate={it.expiry_date} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Log */}
      <div className="border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <p className="veta-tag">Historial de envíos</p>
          <h3 className="font-display text-2xl mt-1">Registro de Alertas</h3>
        </div>
        {logs.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">Aún no se han enviado alertas.</p>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((l) => (
              <div key={l.id} className="p-4 flex flex-wrap items-center justify-between gap-4" data-testid={`alert-log-${l.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-black truncate">{l.subject}</p>
                  <p className="text-xs text-gray-500 font-mono">→ {l.recipient}</p>
                  <p className="text-xs text-gray-400 mt-1">{fmtDateTime(l.created_at)} · {l.items_count} item(s)</p>
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 ${
                  l.result?.mocked
                    ? "bg-yellow-100 text-yellow-800"
                    : l.result?.status === "sent"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}>
                  {l.result?.mocked ? "MOCK" : l.result?.status || "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Activation hint */}
      {!status.live && (
        <div className="mt-8 border-2 border-blue-600 bg-blue-50 p-6">
          <p className="veta-tag text-blue-800 mb-2">Activar envíos reales</p>
          <h3 className="font-display text-xl">Cómo poner Alertas Pro en modo LIVE</h3>
          <ol className="mt-3 text-sm text-gray-700 space-y-1 list-decimal pl-5">
            <li>Crea una cuenta en <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-blue-700 underline">resend.com</a> (plan gratis incluye 3.000 correos/mes).</li>
            <li>Genera una API Key (empieza con <code className="font-mono bg-white px-1">re_...</code>).</li>
            <li>Pídele al equipo técnico agregarla a <code className="font-mono bg-white px-1">backend/.env</code> como <code className="font-mono bg-white px-1">RESEND_API_KEY=...</code> y reiniciar el servicio.</li>
            <li>Verifica un dominio en Resend para usar tu propio remitente (<code className="font-mono bg-white px-1">SENDER_EMAIL</code>).</li>
          </ol>
        </div>
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}
